import type React from "react";
import { useParams } from "react-router-dom";

import ErrorComponent from "components/app/Error/ErrorComponent";
import { VIEW_POLICIES } from "constants/rbac";
import { useAuthorizationCheck } from "hooks/ui/rbac/useAuthorizationCheck";
import { API_STATUS_CODES } from "legacy/constants/ApiConstants";
import { HOME_URL } from "legacy/constants/routes";
import ErrorPageHeader from "legacy/pages/common/ErrorPageHeader";
import { ForbiddenPage } from "pages/components/ForbiddenPage";
import { useListApplicationsQuery } from "store/slices/reduxApi/applications";

export const SecurityCenterAuthorizedPageWrapper: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { applicationId } = useParams();
  const [canViewPolicies] = useAuthorizationCheck([VIEW_POLICIES]);
  const {
    data: applications = [],
    isError,
    isLoading,
    refetch,
  } = useListApplicationsQuery(undefined, { skip: canViewPolicies });
  const canEditApplication = applications.some(
    (application) =>
      application.isEditable &&
      (applicationId === undefined || application.id === applicationId),
  );

  if (isLoading) {
    return null;
  }

  if (isError) {
    return (
      <>
        <ErrorPageHeader />
        <ErrorComponent
          errorCode={API_STATUS_CODES.SERVER_ERROR}
          title="Couldn't verify access"
          errorMessage="We couldn't load your application permissions. Try again."
          buttonText="Try again"
          handleButtonClick={() => {
            void refetch();
          }}
          secondaryButtonText="Return home"
          secondaryButtonAction={() => (window.location.pathname = HOME_URL)}
        />
      </>
    );
  }

  if (!canViewPolicies && !canEditApplication) {
    const applicationAccess = applicationId
      ? "this application"
      : "at least one application";
    return (
      <ForbiddenPage
        message={`You need permission to edit ${applicationAccess} or view policies to use this feature. Ask your admins to update your permissions.`}
      />
    );
  }

  return <>{children}</>;
};
